// src/services/portfolioService.js
// One-shot portfolio data for the Portfolio dashboard. The backend aggregates
// every exchange + holding server-side (cached), so the page loads with a single
// request instead of orchestrating many live-exchange round-trips.
import http, { API_BASE } from './http';

const portfolioService = {
  /**
   * Full portfolio model: { asOf, exchanges:[{...holdings}], aggregate }.
   * Scopes (all vs single exchange) are derived client-side from this payload.
   */
  async summary({ fresh = false } = {}) {
    const res = await http.get(`${API_BASE}/api/portfolio/summary`, {
      params: fresh ? { fresh: 1 } : undefined,
    });
    return res.data;
  },

  /**
   * Equity series for the chart.
   * @param {'1D'|'1W'|'1M'|'1Y'} range
   * @param {number|null} exchangeId  null/undefined = whole-account aggregate
   * @returns {Promise<{range, scope, series:[{t,v}]}>}
   */
  async history(range = '1M', exchangeId = null) {
    const params = { range };
    if (exchangeId != null) params.exchange_id = exchangeId;
    const res = await http.get(`${API_BASE}/api/portfolio/history`, { params });
    return res.data;
  },

  /**
   * Live factor / sector exposure of the stock holdings (FF-6 betas from the
   * market-data lake, sector weights, limit breaches). Cached server-side
   * 15 min; the api answers 200 with `available:false` + `reason` when the
   * lake is unset/down or there are no stock holdings.
   * @param {number|null} exchangeId  null = whole-account aggregate
   */
  async exposure(exchangeId = null, { fresh = false } = {}) {
    const params = {};
    if (exchangeId != null) params.exchange_id = exchangeId;
    if (fresh) params.fresh = 1;
    const res = await http.get(`${API_BASE}/api/portfolio/exposure`, { params });
    return res.data;
  },
};

export default portfolioService;
